/** Wire Feature 2 command flow into assistant messages. */

import { format, addDays } from "date-fns";
import { roleCanWrite } from "assistant/userRole";
import { classifyWriteIntent, isLlmEnabled } from "assistant/llmClient";
import { extractPendingLeaveDraft } from "assistant/conversationContext";
import {
  parseWriteCommand,
  parseLeaveDetailsReply,
  buildCommandPayload,
  runCommandPreview,
  runCommandExecute,
  loadCommandHistory,
  looksLikeWriteCommand,
  smellsLikeWriteIntent,
  draftFromClassifiedIntent,
} from "assistant/commandFlow";

function todayIso() {
  return format(new Date(), "yyyy-MM-dd");
}

function msg(partial) {
  return {
    id: partial.id || `msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    role: "assistant",
    type: "text",
    content: "",
    actions: [],
    cards: [],
    suggestions: [],
    teacherResults: [],
    timestamp: Date.now(),
    ...partial,
  };
}

function errorMessage(language, code, fallback, meta = {}) {
  const fr = language !== "en";
  const apiMsg = String(fallback || "").trim();
  const map = {
    TEACHER_NOT_FOUND: fr ? "Enseignante introuvable (ou hors périmètre)." : "Teacher not found (or out of scope).",
    ESTABLISHMENT_NOT_FOUND: fr ? "Établissement introuvable." : "Establishment not found.",
    LEAVE_NOT_FOUND: fr ? "Aucune demande de congé en attente trouvée." : "No pending leave request found.",
    LEAVE_TYPE_NOT_FOUND: fr ? "Type de congé introuvable." : "Leave type not found.",
    ALREADY_ASSIGNED: fr ? "Déjà affectée." : "Already assigned.",
    NOT_ASSIGNED: fr ? "Aucune affectation active à retirer." : "No active assignment to remove.",
    NOT_LICENSED: fr ? "Cette enseignante n'a pas de licence active." : "This teacher is not licensed.",
    NO_LICENSE_SLOT: fr ? "Plus de créneau licence disponible." : "No license slots remaining.",
    FORBIDDEN: fr ? "Action non autorisée pour votre rôle." : "Not allowed for your role.",
    VALIDATION_ERROR: fr ? "Données invalides." : "Invalid data.",
    UNSUPPORTED_COMMAND: fr ? "Commande non supportée." : "Unsupported command.",
    TOKEN_INVALID: fr ? "Jeton invalide — relancez la commande." : "Invalid token — run the command again.",
    TOKEN_EXPIRED: fr ? "Jeton expiré — relancez la commande." : "Token expired — run the command again.",
    TOKEN_USED: fr ? "Cette confirmation a déjà été utilisée." : "This confirmation was already used.",
    EXECUTE_FAILED: fr ? "Échec de l'exécution." : "Execution failed.",
  };
  const base = map[code] || apiMsg || (fr ? "Action impossible." : "Action failed.");
  const extras = [];
  if (meta.traceId) extras.push(fr ? `TraceId : ${meta.traceId}` : `TraceId: ${meta.traceId}`);
  if (meta.httpStatus) extras.push(`HTTP ${meta.httpStatus}`);
  const suffix = extras.length ? `\n\n${extras.join(" · ")}` : "";
  if (apiMsg && apiMsg !== base && !base.includes(apiMsg)) {
    return `${base}\n\n${apiMsg}${suffix}`;
  }
  return `${base}${suffix}`;
}

function isAlreadyLicensedMessage(text) {
  return /already\s+licen|déjà\s+licenci|deja\s+licenci|already\s+active/i.test(String(text || ""));
}

function resultRoute(command) {
  if (command === "assign_teacher" || command === "unassign_teacher") return "/affectations";
  if (command === "update_leave_status" || command === "create_leave_request") return "/leave-requests";
  if (command === "activate_license" || command === "deactivate_license") return "/teacher-license";
  return "/dashboard";
}

function resultActionLabel(command, fr) {
  if (command === "assign_teacher" || command === "unassign_teacher") {
    return fr ? "Voir affectations" : "View assignments";
  }
  if (command === "update_leave_status" || command === "create_leave_request") {
    return fr ? "Voir congés" : "View leave";
  }
  if (command === "activate_license" || command === "deactivate_license") {
    return fr ? "Voir licences" : "View licenses";
  }
  return fr ? "Tableau de bord" : "Dashboard";
}

function previewToConfirmMessage(preview, language, command, payload) {
  const fr = language !== "en";
  if (!preview.success || !preview.confirmToken) {
    const apiMsg = String(preview.message || "").trim();
    const code = preview.errorCode;

    if (code === "VALIDATION_ERROR" && isAlreadyLicensedMessage(apiMsg)) {
      return msg({
        type: "info",
        content: fr
          ? `Cette enseignante a déjà une licence active (teacherId ${payload?.teacherId ?? "—"}).`
          : `This teacher is already licensed (teacherId ${payload?.teacherId ?? "—"}).`,
        suggestions: [
          fr ? "Désactiver la licence pour 600700" : "Deactivate license for 600700",
          fr ? "Quota licences" : "License quota",
        ],
      });
    }

    if (code === "NOT_LICENSED") {
      return msg({
        type: "info",
        content: errorMessage(language, code, apiMsg),
        suggestions: [
          fr ? "Activer la licence pour 600700" : "Activate license for 600700",
          fr ? "Quota licences" : "License quota",
        ],
      });
    }

    if (code === "NOT_ASSIGNED") {
      return msg({
        type: "info",
        content: errorMessage(language, code, apiMsg),
        suggestions: [
          fr ? "Affecter 600700 à AWINE GASSAR" : "Assign 600700 to AWINE GASSAR",
          fr ? "Chercher enseignant 600700" : "Search teacher 600700",
        ],
      });
    }

    let hint = "";
    if (code === "VALIDATION_ERROR" && payload?.teacherId && !apiMsg) {
      hint = fr
        ? `\n\nEnvoyé: teacherId=${payload.teacherId}. Vérifiez que c'est l'ID base (pas le matricule).`
        : `\n\nSent: teacherId=${payload.teacherId}. Confirm this is the DB id (not matricule).`;
    }

    return msg({
      type: "error",
      content: errorMessage(language, code, preview.message) + hint,
      suggestions: [
        fr ? "Désactiver la licence pour 600700" : "Deactivate license for 600700",
        fr ? "Créer un congé pour 600700 du 2026-08-01 au 2026-08-05 Medical" : "Create leave for 600700 from 2026-08-01 to 2026-08-05 Medical",
        fr ? "Désaffecter 600700" : "Unassign 600700",
      ],
    });
  }

  const expiresAt = Date.now() + Math.max(30, Number(preview.expiresInSeconds) || 120) * 1000;
  return msg({
    type: "command_confirm",
    content: fr
      ? "Vérifiez le brouillon ci-dessous, puis confirmez pour exécuter."
      : "Review the draft below, then confirm to execute.",
    commandConfirm: {
      command: preview.command || command,
      confirmToken: preview.confirmToken,
      summary: preview.summary || {},
      warnings: preview.warnings || [],
      expiresAt,
      consumed: false,
    },
  });
}

async function previewFromDraft(draft, language) {
  const built = await buildCommandPayload(draft);
  if (!built.ok) {
    return pickOrErrorMessage(built, language);
  }
  console.info("[Assistant] command preview", built.command, built.payload);
  const preview = await runCommandPreview(built.command, built.payload);
  return previewToConfirmMessage(preview, language, built.command, built.payload);
}

function pickOrErrorMessage(built, language) {
  const fr = language !== "en";
  const draft = built.draft;

  if (built.reason === "pick_teacher") {
    return msg({
      type: "command_pick",
      content: fr
        ? "Plusieurs enseignantes correspondent. Cliquez pour continuer :"
        : "Several teachers match. Click one to continue:",
      teacherResults: built.matches,
      teacherResultsSelectable: true,
      commandDraft: draft,
      pickKind: "teacher",
    });
  }

  if (built.reason === "pick_establishment") {
    return msg({
      type: "command_pick",
      content: fr
        ? "Plusieurs établissements correspondent. Cliquez pour continuer :"
        : "Several establishments match. Click one to continue:",
      pickResults: built.matches.map((e) => ({
        id: e.id,
        name: e.name,
        reg: e.code || "",
        detail: e.detail || "",
      })),
      pickKind: "establishment",
      commandDraft: draft,
    });
  }

  if (built.reason === "pick_leave") {
    return msg({
      type: "command_pick",
      content: fr
        ? "Plusieurs congés en attente. Cliquez pour continuer :"
        : "Several pending leave requests. Click one to continue:",
      pickResults: built.matches.map((l) => ({
        id: l.id,
        name: `${l.leaveType} · ${l.startDate} → ${l.endDate}`,
        reg: `#${l.id}`,
        detail: l.teacherName,
      })),
      pickKind: "leave",
      commandDraft: draft,
    });
  }

  if (built.reason === "pick_leave_type") {
    return msg({
      type: "command_pick",
      content: fr
        ? "Plusieurs types de congé. Cliquez pour continuer :"
        : "Several leave types match. Click one to continue:",
      pickResults: built.matches.map((t) => ({
        id: t.id,
        name: t.name,
        reg: `#${t.id}`,
        detail: fr ? "Type de congé" : "Leave type",
      })),
      pickKind: "leave_type",
      commandDraft: draft,
    });
  }

  const contentMap = {
    teacher_not_found: fr
      ? `Enseignante introuvable pour « ${built.query || ""} ».`
      : `Teacher not found for « ${built.query || ""} ».`,
    establishment_not_found: fr
      ? `Établissement introuvable pour « ${built.query || ""} ». Ouvrez Établissements pour le nom exact, puis réessayez.`
      : `Establishment not found for « ${built.query || ""} ». Open Establishments for the exact name, then retry.`,
    leave_not_found: fr
      ? "Aucune demande de congé en attente pour cette enseignante."
      : "No pending leave request for this teacher.",
    leave_type_not_found: fr
      ? `Type de congé introuvable${built.query ? ` pour « ${built.query} »` : ""}. Ex. Medical.`
      : `Leave type not found${built.query ? ` for « ${built.query} »` : ""}. e.g. Medical.`,
    leave_dates_required: fr
      ? "Indiquez les dates : Créer un congé pour 600700 du 2026-08-01 au 2026-08-05 Medical"
      : "Include dates: Create leave for 600700 from 2026-08-01 to 2026-08-05 Medical",
    unsupported: fr ? "Commande non supportée." : "Unsupported command.",
  };

  return msg({
    type: "error",
    content: contentMap[built.reason] || (fr ? "Impossible de préparer la commande." : "Could not prepare the command."),
    suggestions: [
      fr ? "Guide affectation" : "Assignment guide",
      fr ? "Guide congé" : "Leave guide",
      fr ? "Quota licences" : "License quota",
    ],
  });
}

export function shouldHandleWriteCommand(input) {
  return looksLikeWriteCommand(input) || smellsLikeWriteIntent(input);
}

async function resolveWriteDraft(input, context) {
  const parsed = parseWriteCommand(input);
  if (parsed) return parsed;

  if (!context.aiRoutingEnabled || !isLlmEnabled() || !smellsLikeWriteIntent(input)) return null;

  try {
    const classified = await classifyWriteIntent(input, context);
    if (!classified?.ok) return null;
    const draft = draftFromClassifiedIntent(classified.classified);
    if (draft) {
      console.info("[Assistant] LLM classified write intent", draft.command, draft.hints);
    }
    return draft;
  } catch (err) {
    console.warn("[Assistant] classify write intent failed", err?.message || err);
    return null;
  }
}

export async function executeClassifiedWriteDraft(draft, context) {
  const { language, roleBucket } = context;
  const fr = language !== "en";

  if (!draft || draft.kind !== "command") return null;
  if (!roleCanWrite(roleBucket)) {
    return msg({
      type: "error",
      content: fr
        ? "Votre rôle (observateur) ne peut pas exécuter d'actions d'écriture."
        : "Your role (observer) cannot run write actions.",
    });
  }

  try {
    return await previewFromDraft(draft, language);
  } catch (err) {
    console.error("[Assistant] AI-routed write command failed", err);
    return null;
  }
}

export async function handleLeaveDetailsReply(input, context) {
  const { language, roleBucket, messages } = context;
  const fr = language !== "en";
  if (!roleCanWrite(roleBucket)) return null;

  const pending = extractPendingLeaveDraft(messages);
  if (!pending) return null;

  const parsed = parseLeaveDetailsReply(input, pending);
  if (!parsed) {
    return msg({
      type: "command_prompt",
      content: fr
        ? "Je n'ai pas reconnu les dates. Utilisez le format **AAAA-MM-JJ**.\n\nExemple : « Congé maladie du 2026-08-10 au 2026-08-12 »"
        : "I couldn't read those dates. Use **YYYY-MM-DD**.\n\nExample: « Sick leave from 2026-08-10 to 2026-08-12 »",
      commandDraft: pending,
      suggestions: pending.suggestions || [],
    });
  }

  try {
    return await previewFromDraft({ command: parsed.command, hints: parsed.hints }, language);
  } catch (err) {
    console.error("[Assistant] leave details reply failed", err);
    return null;
  }
}

function leaveDetailsPrompt(parsed, language) {
  const fr = language !== "en";
  const teacher = parsed.hints?.teacherQuery || (fr ? "l'enseignante" : "the teacher");
  const start = todayIso();
  const end = format(addDays(new Date(), 2), "yyyy-MM-dd");
  const example = fr
    ? `Congé maladie du ${start} au ${end}`
    : `Sick leave from ${start} to ${end}`;

  return msg({
    type: "command_prompt",
    content: fr
      ? `Pour **${teacher}**, précisez le **type de congé** et les **dates** (début et fin).\n\nExemple : « ${example} »`
      : `For **${teacher}**, please provide the **leave type** and **dates** (start and end).\n\nExample: « ${example} »`,
    commandDraft: {
      command: parsed.command,
      hints: parsed.hints,
      awaiting: "leave_details",
    },
    suggestions: [example],
  });
}

export async function handleWriteCommandMessage(input, context) {
  const { language, roleBucket } = context;
  const fr = language !== "en";

  const parsed = await resolveWriteDraft(input, context);
  if (!parsed) return null;

  if ((parsed.kind === "await_leave_details" || parsed.kind === "command") && !roleCanWrite(roleBucket)) {
    return msg({
      type: "error",
      content: fr
        ? "Votre rôle (observateur) ne peut pas exécuter d'actions d'écriture."
        : "Your role (observer) cannot run write actions.",
    });
  }

  if (parsed.kind === "await_leave_details") {
    return leaveDetailsPrompt(parsed, language);
  }

  if (parsed.kind === "history") {
    const hist = await loadCommandHistory(language);
    if (!hist.ok) {
      return msg({ type: "error", content: hist.message });
    }
    if (!hist.rows.length) {
      return msg({
        content: fr ? "Aucune commande assistant récente." : "No recent assistant commands.",
      });
    }
    const lines = hist.rows
      .slice(0, 10)
      .map((r) => {
        const when = String(r.createdAt ?? r.CreatedAt ?? "").slice(0, 16).replace("T", " ");
        const ok = r.success ?? r.Success;
        const summary = r.summary ?? r.Summary ?? r.command ?? r.Command;
        const user = r.userName ?? r.UserName ?? "";
        return `• ${when} — ${ok ? "✓" : "✗"} ${summary}${user ? ` (${user})` : ""}`;
      })
      .join("\n");
    return msg({
      type: "command_history",
      content: (fr ? "**Historique des commandes**\n\n" : "**Command history**\n\n") + lines,
      actions: [
        {
          id: "nav-licenses",
          label: fr ? "Licences" : "Licenses",
          route: "/teacher-license",
          variant: "secondary",
        },
      ],
    });
  }

  const draft = {
    command: parsed.command,
    hints: parsed.hints || {},
  };

  try {
    return await previewFromDraft(draft, language);
  } catch (err) {
    console.error("[Assistant] write command failed", err);
    const detail = err?.response?.data?.message || err?.response?.data?.Message || err?.message || "";
    return msg({
      type: "error",
      content: fr
        ? `Erreur lors de la préparation de la commande.${detail ? `\n\n${detail}` : ""}`
        : `Error while preparing the command.${detail ? `\n\n${detail}` : ""}`,
      suggestions: [
        fr ? "Désaffecter 600700" : "Unassign 600700",
        fr ? "Désactiver la licence pour 600700" : "Deactivate license for 600700",
      ],
    });
  }
}

/** Continue after user picks teacher / establishment / leave / leave type. */
export async function continueWriteCommand(pick, context) {
  const { language, roleBucket } = context;
  const fr = language !== "en";
  if (!roleCanWrite(roleBucket)) {
    return msg({
      type: "error",
      content: fr ? "Action non autorisée." : "Action not allowed.",
    });
  }

  const draft = { ...(pick.commandDraft || {}) };
  if (pick.teacherId) draft.teacherId = Number(pick.teacherId);
  if (pick.establishmentId) {
    draft.establishmentId = Number(pick.establishmentId);
    draft.hints = { ...(draft.hints || {}), establishmentQuery: undefined };
  }
  if (pick.leaveRequestId) {
    draft.leaveRequestId = Number(pick.leaveRequestId);
  }
  if (pick.leaveTypeId) {
    draft.leaveTypeId = Number(pick.leaveTypeId);
    draft.hints = {
      ...(draft.hints || {}),
      leaveTypeQuery: pick.leaveTypeName || draft.hints?.leaveTypeQuery,
      reason: draft.hints?.reason || pick.leaveTypeName || "Requested via assistant",
    };
  }

  try {
    return await previewFromDraft(draft, language);
  } catch (err) {
    console.error("[Assistant] continue write command failed", err);
    const detail = err?.message || "";
    return msg({
      type: "error",
      content: fr
        ? `Impossible de continuer la commande.${detail ? `\n\n${detail}` : ""}`
        : `Could not continue the command.${detail ? `\n\n${detail}` : ""}`,
      suggestions: [
        fr ? "Désactiver la licence pour 600700" : "Deactivate license for 600700",
      ],
    });
  }
}

export async function executeWriteCommand(commandConfirm, context) {
  const { language } = context;
  const fr = language !== "en";

  if (!commandConfirm?.confirmToken) {
    return msg({
      type: "error",
      content: fr ? "Jeton manquant — relancez la commande." : "Missing token — run the command again.",
    });
  }

  if (commandConfirm.expiresAt && Date.now() > commandConfirm.expiresAt) {
    return msg({
      type: "error",
      content: errorMessage(language, "TOKEN_EXPIRED"),
      suggestions: [
        fr ? "Désaffecter 600700" : "Unassign 600700",
      ],
    });
  }

  const result = await runCommandExecute(commandConfirm.confirmToken, commandConfirm.command);
  if (!result.success) {
    console.error("[Assistant] command execute failed", {
      command: commandConfirm.command,
      errorCode: result.errorCode,
      message: result.message,
      traceId: result.traceId,
      httpStatus: result.httpStatus,
    });
    return msg({
      type: "error",
      content: errorMessage(language, result.errorCode, result.message, {
        traceId: result.traceId,
        httpStatus: result.httpStatus,
      }),
      suggestions: [
        fr ? "Relancer la commande" : "Run the command again",
        fr ? "Historique des commandes" : "Command history",
      ],
    });
  }

  const command = result.command || commandConfirm.command;

  return msg({
    type: "command_result",
    content:
      (result.message && String(result.message).trim()) ||
      (fr ? "Action exécutée avec succès." : "Action completed successfully."),
    cards: result.auditId
      ? [{ label: fr ? "Audit" : "Audit", value: `#${result.auditId}` }]
      : [],
    actions: [
      {
        id: "open-result",
        label: resultActionLabel(command, fr),
        route: resultRoute(command),
        variant: "primary",
      },
    ],
    suggestions: [
      fr ? "Historique des commandes" : "Command history",
      fr ? "Résumé du portail" : "Portal summary",
    ],
  });
}

export function cancelWriteCommandMessage(language) {
  return msg({
    content: language === "en" ? "Action cancelled." : "Action annulée.",
    suggestions: [
      language === "en" ? "Portal summary" : "Résumé du portail",
      language === "en" ? "What can you do?" : "Que peux-tu faire ?",
    ],
  });
}
